/**
 * Main LaTeX Autocomplete Provider
 * Integrates all completion providers and handles routing
 */

import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { parseContext } from './contextParser'
import { analyzePosition } from './positionAnalyzer'
import { multiStageCompletion } from './multiStageProvider'
import { 
  CommandProvider, 
  EnvironmentProvider, 
  MathSymbolProvider, 
  PackageProvider,
  CompletionProvider 
} from './providers'
import { logDebug } from '@utils/logger'

/**
 * Main LaTeX autocomplete provider
 */
export function createLaTeXAutocompleteProvider(documentId?: string) {
  // Initialize providers
  const providers: CompletionProvider[] = [
    new CommandProvider(),
    new EnvironmentProvider(),
    new PackageProvider(),
    new MathSymbolProvider(),
  ]
  
  // Sort by priority (highest first)
  providers.sort((a, b) => b.priority - a.priority)
  
  return (context: CompletionContext): CompletionResult | null => {
    const { state, pos } = context
    const text = state.doc.toString()
    
    const position = analyzePosition(text, pos)
    const latexContext = parseContext(text, pos)
    
    logDebug('[MainProvider] Autocomplete triggered', {
      isInCommand: position.isInCommand,
      commandName: position.commandName,
      isInEnvironment: position.isInEnvironment,
      isAfterBackslash: position.isAfterBackslash,
      inMathMode: latexContext.inMathMode
    })
    
    // Priority 1: Multi-stage completion (environments, \begin)
    // This handles the special case of \begin{} → environment selection
    const multiStageResult = multiStageCompletion(context)
    if (multiStageResult) {
      return multiStageResult
    }
    
    // Priority 2: Try specialized providers in priority order
    for (const provider of providers) {
      if (provider.canHandle(context, position, latexContext)) {
        const result = provider.provide(context, position, latexContext)
        if (result) {
          logDebug('[MainProvider] Provider handled', {
            provider: provider.name,
            optionsCount: result.options.length
          })
          return result
        }
      }
    }
    
    // Note: Citation provider is handled separately in the autocomplete override array
    // since it has async behavior and documentId dependency
    
    return null
  }
}
